import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, format, parseISO } from 'date-fns';
import { useLocation, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  Loader2,
  Mail,
  MapPin,
  Package,
  User,
} from 'lucide-react';
import type { CourtHireRate } from '@court-sync/shared';
import { hasRole, isValidPhone, normalizePhoneToE164 } from '@court-sync/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  FieldLabel,
  Input,
  PhoneInput,
  PhoneNumberLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@/components/ui';
import { courtHireService, type GuestCourtHireBookingResult, type PublicInventoryAddon } from '@/services/courtHire';
import {
  CourtHireEquipmentAddons,
  sumSelectedAddonCents,
  type SelectedInventoryAddon,
} from '@/components/public/CourtHireEquipmentAddons';
import { PublicStripePaymentConfirm } from '@/components/public/PublicStripePaymentConfirm';
import { PublicOrgSubpageHero } from '@/components/public/PublicOrgSubpageHero';
import { useClubSlug, useClubSlugResolving } from '@/hooks/useClubSlug';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { useOrgHost } from '@/contexts/OrgHostContext';
import { useOrgPublicProfile } from '@/contexts/OrgPublicProfileContext';
import { resolvePublicOrgSlug, shouldPreferOrgCourtHireApi } from '@/lib/resolvePublicOrgSlug';
import { useDefaultPhoneCountry } from '@/hooks/useDefaultPhoneCountry';
import { useClubHost } from '@/contexts/ClubHostContext';
import { usePublicClubProfile } from '@/queries/publicClubProfile';
import { usePublicOrgCourtHireGrid } from '@/queries/publicOrgProfile';
import { publicOrgService } from '@/services/publicOrg';
import { memberPortalService } from '@/services/memberPortal';
import { publicClubMembershipService, type MemberPortalMembership } from '@/services/publicClubMembership';
import { useAuthStore } from '@/store/auth';
import { useOrgMemberStore } from '@/store/orgMember';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { formatCurrency, type Currency } from '@/utils/currency';
import { cn } from '@/lib/utils';
import {
  type CourtHireAvailabilityGrid,
  normalizeTimeHm,
  parseCourtHireSearchParams,
  resolveInitialCourtId,
  type CourtHireHeldBookingSelection,
  matchesCourtHireHeldBooking,
  validateCourtHireBookingAgainstGrid,
} from '@/lib/courtHirePrefill';
import { courtHireCheckoutSessionKey } from '@/lib/courtHireCheckoutSession';
import { useEmbedHost } from '@/contexts/EmbedHostContext';

type CheckoutStep = 'details' | 'payment' | 'success';

interface CourtHireCheckoutSession {
  pendingPayment: GuestCourtHireBookingResult;
  heldBookingSelection: CourtHireHeldBookingSelection;
  pendingAmountCents: number;
  pendingCurrency: string;
  pendingTaxAmountCents: number;
  pendingTaxInclusive: boolean;
  memberDiscountPercent: number;
}

function readCourtHireCheckoutSession(clubSlug: string): CourtHireCheckoutSession | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(courtHireCheckoutSessionKey(clubSlug));
    if (!raw) return null;
    return JSON.parse(raw) as CourtHireCheckoutSession;
  } catch {
    return null;
  }
}

function writeCourtHireCheckoutSession(clubSlug: string, session: CourtHireCheckoutSession): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(courtHireCheckoutSessionKey(clubSlug), JSON.stringify(session));
}

function clearCourtHireCheckoutSession(clubSlug: string): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(courtHireCheckoutSessionKey(clubSlug));
}

const DURATION_OPTIONS = [
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
] as const;

const ALLOWED_DURATIONS = DURATION_OPTIONS.map((option) => option.value);

function snapDuration(minutes: number): number {
  if (ALLOWED_DURATIONS.includes(minutes as (typeof ALLOWED_DURATIONS)[number])) {
    return minutes;
  }
  return ALLOWED_DURATIONS.reduce((closest, current) =>
    Math.abs(current - minutes) < Math.abs(closest - minutes) ? current : closest
  );
}

function formatCents(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency as Currency);
}

function applyPercentDiscount(priceCents: number, percent: number): number {
  if (percent <= 0) return priceCents;
  return Math.max(0, Math.round(priceCents * (1 - percent / 100)));
}

function resolveMemberCourtHireDiscountPercent(
  memberships: MemberPortalMembership[] | undefined
): number {
  if (!memberships?.length) return 0;
  return memberships
    .filter((membership) => membership.status === 'active' && membership.paymentStatus === 'paid')
    .reduce(
      (max, membership) =>
        Math.max(max, membership.membershipType?.courtHireDiscountPercent ?? 0),
      0
    );
}

function SectionHeading({ icon: Icon, title, description }: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
        <Icon className="h-4 w-4 text-primary" aria-hidden />
      </div>
      <div>
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
    </div>
  );
}

function PublicClubCourtHireContent({ embedded = false }: { embedded?: boolean }) {
  const { isEmbedMode } = useEmbedHost();
  const minimalChrome = embedded || isEmbedMode;
  const slug = useClubSlug();
  const orgSlugFromHook = useOrgSlug();
  const slugResolving = useClubSlugResolving();
  const defaultPhoneCountry = useDefaultPhoneCountry();
  const { user } = useAuthStore();
  const { clubSlug: storeClubSlug } = useOrgMemberStore();
  const consumerNav = useUnifiedConsumerNav();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialPrefillRef = useRef(parseCourtHireSearchParams(searchParams));
  const initialDate = initialPrefillRef.current?.date ?? format(new Date(), 'yyyy-MM-dd');
  const { host, loading: hostLoading } = useClubHost();
  const {
    host: orgHost,
    organization: orgHostOrganization,
    pathSlug: orgHostPathSlug,
  } = useOrgHost();
  const { profile: orgProfile } = useOrgPublicProfile();
  const { profile } = usePublicClubProfile({
    slug,
    subdomainLabel: host.mode === 'subdomain' ? host.subdomainLabel : undefined,
    hostLoading: host.mode === 'subdomain' && hostLoading,
    enabled: !!slug || (host.mode === 'subdomain' && !!host.subdomainLabel),
  });
  const club = profile?.club;

  const resolvedOrgSlug = resolvePublicOrgSlug({
    orgSlugParam: orgSlugFromHook,
    pathname: location.pathname,
    orgProfileSlug: orgProfile?.organization.slug,
    orgHostPathSlug,
    orgHostOrganizationSlug: orgHostOrganization?.slug,
    isSubdomainHost: orgHost.mode === 'subdomain',
  });

  const preferOrgApi = shouldPreferOrgCourtHireApi({
    orgProfile,
    orgHostOrganization,
    isSubdomainHost: orgHost.mode === 'subdomain',
    subdomainLabel: orgHost.subdomainLabel,
    pathname: location.pathname,
    resolvedOrgSlug,
  });

  const bookingClubSlug = storeClubSlug ?? orgProfile?.clubSlug ?? slug;
  const guestCheckoutSlug = bookingClubSlug ?? slug;
  const canLoadCourtHire = preferOrgApi ? Boolean(resolvedOrgSlug) : Boolean(guestCheckoutSlug);
  const memberCourtHireEligible =
    consumerNav.hasCourtHire ||
    Boolean(user && hasRole(user, 'member') && orgProfile?.modules.courtHireEntitled);
  const useMemberCheckout = Boolean(user) && Boolean(bookingClubSlug) && memberCourtHireEligible;

  const membershipQuery = useQuery({
    queryKey: ['court-hire-member-discount', bookingClubSlug],
    enabled: useMemberCheckout && Boolean(bookingClubSlug),
    queryFn: async () => {
      const res = await publicClubMembershipService.getMyMembership(bookingClubSlug!);
      return res.success ? res.data : null;
    },
  });

  const previewMemberDiscountPercent = resolveMemberCourtHireDiscountPercent(
    membershipQuery.data?.memberships
  );
  const hasActivePaidMembership = Boolean(
    membershipQuery.data?.memberships?.some(
      (membership) => membership.status === 'active' && membership.paymentStatus === 'paid'
    )
  );

  const [rates, setRates] = useState<CourtHireRate[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [courtId, setCourtId] = useState('');
  const [bookingDate, setBookingDate] = useState(initialDate);
  const [startTime, setStartTime] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [pendingPayment, setPendingPayment] = useState<GuestCourtHireBookingResult | null>(null);
  const [heldBookingSelection, setHeldBookingSelection] =
    useState<CourtHireHeldBookingSelection | null>(null);
  const [pendingAmountCents, setPendingAmountCents] = useState(0);
  const [pendingCurrency, setPendingCurrency] = useState('USD');
  const [pendingTaxAmountCents, setPendingTaxAmountCents] = useState(0);
  const [pendingTaxInclusive, setPendingTaxInclusive] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>('details');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<SelectedInventoryAddon[]>([]);
  const [availableAddons, setAvailableAddons] = useState<PublicInventoryAddon[]>([]);
  const [hasHireableEquipment, setHasHireableEquipment] = useState(false);
  const [memberDiscountPercent, setMemberDiscountPercent] = useState(0);
  const restoredCheckoutRef = useRef(false);
  const appliedPrefillRef = useRef(false);

  const {
    grid: orgAvailabilityGrid,
    isLoading: orgGridLoading,
    error: orgGridError,
    refetch: refetchOrgGrid,
  } = usePublicOrgCourtHireGrid(
    resolvedOrgSlug,
    bookingDate,
    undefined,
    undefined,
    preferOrgApi && Boolean(resolvedOrgSlug) && Boolean(bookingDate)
  );

  const clubGridQuery = useQuery({
    queryKey: ['publicClubCourtHireGrid', guestCheckoutSlug, bookingDate],
    enabled: !preferOrgApi && Boolean(guestCheckoutSlug) && Boolean(bookingDate),
    queryFn: async () => {
      const res = await courtHireService.getPublicAvailabilityGrid(guestCheckoutSlug!, {
        date: bookingDate,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load court availability');
      }
      return res.data;
    },
    staleTime: 30_000,
  });

  const availabilityGrid: CourtHireAvailabilityGrid | null = preferOrgApi
    ? orgAvailabilityGrid
    : clubGridQuery.data ?? null;
  const gridLoading = preferOrgApi ? orgGridLoading : clubGridQuery.isLoading;
  const gridError = preferOrgApi
    ? orgGridError
    : clubGridQuery.error instanceof Error
      ? clubGridQuery.error.message
      : clubGridQuery.error
        ? String(clubGridQuery.error)
        : null;

  const courtOptions = useMemo(
    () =>
      availabilityGrid?.courts.map((court) => ({ id: court.id, name: court.name })) ?? [],
    [availabilityGrid]
  );

  const availabilityMessage = useMemo(() => {
    if (gridLoading) return null;
    if (gridError) return gridError;
    if (!availabilityGrid) {
      return preferOrgApi && !resolvedOrgSlug
        ? null
        : 'Court hire is not available right now. Check that Stripe Connect, court hire rates, and guest checkout are enabled.';
    }
    if (courtOptions.length === 0) {
      return 'No courts are marked as available for hire yet. Enable court hire on each court in Courts & locations.';
    }
    return null;
  }, [
    availabilityGrid,
    courtOptions.length,
    gridError,
    gridLoading,
    preferOrgApi,
    resolvedOrgSlug,
  ]);

  const checkoutSlug = bookingClubSlug ?? slug;

  const clearCheckoutSession = useCallback(() => {
    if (checkoutSlug) clearCourtHireCheckoutSession(checkoutSlug);
    setPendingPayment(null);
    setHeldBookingSelection(null);
    setMemberDiscountPercent(0);
    setPendingAmountCents(0);
    setPendingTaxAmountCents(0);
    setPendingTaxInclusive(false);
  }, [checkoutSlug]);

  useEffect(() => {
    if (!useMemberCheckout || !bookingClubSlug || !user) return;
    if (user.email) setGuestEmail(user.email);
    if (user.firstName) setGuestFirstName(user.firstName);
    if (user.lastName) setGuestLastName(user.lastName);
  }, [useMemberCheckout, bookingClubSlug, user]);

  useEffect(() => {
    if (!checkoutSlug || restoredCheckoutRef.current || pendingPayment) return;
    const session = readCourtHireCheckoutSession(checkoutSlug);
    if (session?.pendingPayment?.clientSecret && session.heldBookingSelection) {
      restoredCheckoutRef.current = true;
      setPendingPayment(session.pendingPayment);
      setHeldBookingSelection(session.heldBookingSelection);
      setPendingAmountCents(session.pendingAmountCents);
      setPendingCurrency(session.pendingCurrency);
      setPendingTaxAmountCents(session.pendingTaxAmountCents ?? 0);
      setPendingTaxInclusive(session.pendingTaxInclusive ?? false);
      setMemberDiscountPercent(session.memberDiscountPercent);
      setBookingDate(session.heldBookingSelection.bookingDate);
      setCourtId(session.heldBookingSelection.courtId);
      setStartTime(session.heldBookingSelection.startTime);
      setDurationMinutes(session.heldBookingSelection.durationMinutes);
      return;
    }

    if (!useMemberCheckout) return;
    void memberPortalService.cancelPendingCourtHireBookings(checkoutSlug).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['org-consumer-court-bookings'] });
      void queryClient.invalidateQueries({ queryKey: ['org-consumer-inventory-hires'] });
    }).catch(() => undefined);
  }, [checkoutSlug, pendingPayment, useMemberCheckout]);

  useEffect(() => {
    if (!checkoutSlug || !pendingPayment || !heldBookingSelection) return;
    writeCourtHireCheckoutSession(checkoutSlug, {
      pendingPayment,
      heldBookingSelection,
      pendingAmountCents,
      pendingCurrency,
      pendingTaxAmountCents,
      pendingTaxInclusive,
      memberDiscountPercent,
    });
  }, [
    checkoutSlug,
    pendingPayment,
    heldBookingSelection,
    pendingAmountCents,
    pendingCurrency,
    pendingTaxAmountCents,
    pendingTaxInclusive,
    memberDiscountPercent,
  ]);

  useEffect(() => {
    if (!availabilityGrid) return;
    const prefill = initialPrefillRef.current;
    const courts = availabilityGrid.courts.map((court) => ({ id: court.id, name: court.name }));
    setCourtId((current) => {
      if (current && courts.some((court) => court.id === current)) return current;
      return resolveInitialCourtId(courts, prefill?.courtId);
    });
    if (!appliedPrefillRef.current && prefill) {
      setStartTime(prefill.startTime);
      setDurationMinutes(snapDuration(prefill.durationMinutes));
      appliedPrefillRef.current = true;
    }
  }, [availabilityGrid]);

  useEffect(() => {
    if (!canLoadCourtHire || (slugResolving && !resolvedOrgSlug)) return undefined;

    let cancelled = false;
    const loadRates = async () => {
      const ratesPromise =
        useMemberCheckout && bookingClubSlug && hasActivePaidMembership
          ? memberPortalService.getCourtHireRates(bookingClubSlug)
          : guestCheckoutSlug
            ? courtHireService.getPublicAvailability(guestCheckoutSlug, {
                from: new Date().toISOString(),
                to: addDays(new Date(), 7).toISOString(),
              })
            : Promise.resolve({ success: false as const, error: 'Court hire unavailable' });

      const ratesRes = await ratesPromise;
      if (cancelled) return;

      if (ratesRes.success && ratesRes.data) {
        if (Array.isArray(ratesRes.data)) {
          setRates(ratesRes.data);
        } else if ('rates' in ratesRes.data) {
          setRates(ratesRes.data.rates ?? []);
        }
      }
    };

    void loadRates();
    return () => {
      cancelled = true;
    };
  }, [
    canLoadCourtHire,
    guestCheckoutSlug,
    resolvedOrgSlug,
    slugResolving,
    useMemberCheckout,
    bookingClubSlug,
    hasActivePaidMembership,
  ]);

  const fetchAvailabilityGrid = useCallback(
    async (date: string): Promise<CourtHireAvailabilityGrid | null> => {
      if (preferOrgApi) {
        if (!resolvedOrgSlug) return null;
        const gridRes = await publicOrgService.getCourtHireAvailabilityGrid(resolvedOrgSlug, { date });
        if (!gridRes.success || !gridRes.data) return null;
        return gridRes.data;
      }
      if (!guestCheckoutSlug) return null;
      const gridRes = await courtHireService.getPublicAvailabilityGrid(guestCheckoutSlug, { date });
      if (!gridRes.success || !gridRes.data) return null;
      return gridRes.data;
    },
    [guestCheckoutSlug, preferOrgApi, resolvedOrgSlug]
  );

  const handleBookingDateChange = (nextDate: string) => {
    setBookingDate(nextDate);
  };

  const selectedCourt = useMemo(
    () => courtOptions.find((court) => court.id === courtId),
    [courtOptions, courtId]
  );

  const selectedRate = useMemo(
    () => rates.find((rate) => rate.courtId === courtId) ?? rates[0] ?? null,
    [rates, courtId]
  );

  const bookingSummary = useMemo(() => {
    if (!bookingDate || !startTime) return null;
    const startAt = new Date(`${bookingDate}T${startTime}`);
    if (Number.isNaN(startAt.getTime())) return null;
    const endAt = new Date(startAt.getTime() + durationMinutes * 60 * 1000);
    const durationLabel =
      DURATION_OPTIONS.find((option) => option.value === durationMinutes)?.label ??
      `${durationMinutes} min`;
    return {
      dateLabel: format(parseISO(bookingDate), 'EEE, d MMM yyyy'),
      timeLabel: `${startTime} – ${format(endAt, 'HH:mm')}`,
      durationLabel,
    };
  }, [bookingDate, startTime, durationMinutes]);

  const activeHeldBooking = useMemo(() => {
    if (!heldBookingSelection || !bookingDate || !courtId || !startTime) return null;
    return matchesCourtHireHeldBooking(heldBookingSelection, {
      courtId,
      bookingDate,
      startTime,
      durationMinutes,
    })
      ? heldBookingSelection
      : null;
  }, [heldBookingSelection, courtId, bookingDate, startTime, durationMinutes]);

  const availabilityCheck = useMemo(() => {
    if (!availabilityGrid || !courtId || !bookingDate || !startTime) return null;
    return validateCourtHireBookingAgainstGrid(
      availabilityGrid,
      courtId,
      normalizeTimeHm(startTime),
      durationMinutes,
      activeHeldBooking
    );
  }, [
    availabilityGrid,
    courtId,
    bookingDate,
    startTime,
    durationMinutes,
    activeHeldBooking,
  ]);

  const bookingWindow = useMemo(() => {
    if (!bookingDate || !startTime) return { startAt: null, endAt: null };
    const normalizedStart = normalizeTimeHm(startTime);
    const startAt = new Date(`${bookingDate}T${normalizedStart}`);
    if (Number.isNaN(startAt.getTime())) return { startAt: null, endAt: null };
    const endAt = new Date(startAt.getTime() + durationMinutes * 60 * 1000);
    return { startAt, endAt };
  }, [bookingDate, startTime, durationMinutes]);

  const addonTotalCents = useMemo(() => {
    if (!bookingWindow.startAt || !bookingWindow.endAt) return 0;
    return sumSelectedAddonCents(
      availableAddons,
      selectedAddons,
      bookingWindow.startAt,
      bookingWindow.endAt
    );
  }, [availableAddons, selectedAddons, bookingWindow]);

  const estimatedTotalCents = (selectedRate?.priceCents ?? 0) + addonTotalCents;

  const summaryCurrency = selectedRate?.currency ?? pendingCurrency ?? 'USD';
  const courtPriceCents = selectedRate?.priceCents ?? 0;
  const effectiveDiscountPercent =
    checkoutStep === 'payment' && memberDiscountPercent > 0
      ? memberDiscountPercent
      : previewMemberDiscountPercent;
  const discountedCourtCents = applyPercentDiscount(courtPriceCents, effectiveDiscountPercent);
  const courtDiscountCents = courtPriceCents - discountedCourtCents;
  const displayTotalCents =
    checkoutStep === 'payment' && pendingAmountCents > 0
      ? pendingAmountCents
      : discountedCourtCents + addonTotalCents;
  const listSubtotalCents = courtPriceCents + addonTotalCents;
  const hasMemberCourtDiscount = effectiveDiscountPercent > 0 && courtDiscountCents > 0;
  const hasPricing = courtPriceCents > 0 || addonTotalCents > 0 || displayTotalCents > 0;
  const requiresPayment = displayTotalCents > 0;

  const addonClubSlug = guestCheckoutSlug ?? slug;

  useEffect(() => {
    if (!addonClubSlug || slugResolving) {
      setHasHireableEquipment(false);
      return undefined;
    }

    let cancelled = false;
    void courtHireService.getPublicAddonCatalog(addonClubSlug).then((res) => {
      if (!cancelled && res.success && res.data) {
        setHasHireableEquipment(res.data.hasHireableEquipment);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [addonClubSlug, slugResolving]);

  useEffect(() => {
    if (!addonClubSlug || !bookingWindow.startAt || !bookingWindow.endAt) {
      setAvailableAddons([]);
      return undefined;
    }
    let cancelled = false;
    void courtHireService
      .getPublicAddons(addonClubSlug, {
        startAt: bookingWindow.startAt.toISOString(),
        endAt: bookingWindow.endAt.toISOString(),
      })
      .then((res) => {
        if (!cancelled && res.success && res.data) {
          setAvailableAddons(res.data);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [addonClubSlug, bookingWindow.startAt, bookingWindow.endAt]);

  useEffect(() => {
    setSelectedAddons((prev) =>
      prev.filter((entry) =>
        availableAddons.some(
          (item) => item.id === entry.itemId && entry.quantity <= item.availableQuantity
        )
      )
    );
  }, [availableAddons]);

  const canSubmit =
    !!courtId &&
    !!bookingDate &&
    !!startTime &&
    (useMemberCheckout ||
      (guestEmail &&
        guestFirstName &&
        guestLastName &&
        guestPhone.trim())) &&
    courtOptions.length > 0 &&
    availabilityCheck?.available !== false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug && !bookingClubSlug) return;

    const normalizedStart = normalizeTimeHm(startTime);
    const selectionMatchesHeld = Boolean(
      pendingPayment &&
        matchesCourtHireHeldBooking(heldBookingSelection, {
          courtId,
          bookingDate,
          startTime: normalizedStart,
          durationMinutes,
        })
    );

    if (selectionMatchesHeld) {
      setCheckoutStep('payment');
      return;
    }

    if (!canSubmit) {
      toast.error('Please complete all required fields');
      return;
    }

    let guestPhoneE164: string | null = null;
    if (!useMemberCheckout) {
      guestPhoneE164 = normalizePhoneToE164(guestPhone, defaultPhoneCountry);
      if (!guestPhoneE164 || !isValidPhone(guestPhone, defaultPhoneCountry)) {
        setPhoneError('Enter a valid mobile number');
        toast.error('Enter a valid mobile number');
        return;
      }
      setPhoneError(null);
    }

    const startAt = new Date(`${bookingDate}T${normalizedStart}`);
    const endAt = new Date(startAt.getTime() + durationMinutes * 60 * 1000);
    if (Number.isNaN(startAt.getTime()) || endAt <= startAt) {
      toast.error('Please choose a valid date and time');
      return;
    }

    setSubmitting(true);
    try {
      const freshGrid = await fetchAvailabilityGrid(bookingDate);
      if (!freshGrid) {
        toast.error('Could not verify court availability. Please try again.');
        return;
      }

      const validation = validateCourtHireBookingAgainstGrid(
        freshGrid,
        courtId,
        normalizedStart,
        durationMinutes
      );
      if (!validation.available) {
        toast.error(validation.reason ?? 'This court is not available at the selected time');
        return;
      }

      const rate = selectedRate;
      const bookingPayload = {
        courtId,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        rateId: rate?.id,
        addons: selectedAddons.filter((a) => a.quantity > 0),
      };

      const res =
        useMemberCheckout && bookingClubSlug
          ? await memberPortalService.createCourtHireBooking(bookingClubSlug, bookingPayload)
          : await courtHireService.createGuestBooking(guestCheckoutSlug!, {
              ...bookingPayload,
              guestEmail,
              guestFirstName,
              guestLastName,
              guestPhone: guestPhoneE164!,
            });
      if (res.success && res.data) {
        const isFreeCheckout =
          res.data.paymentRequired === false ||
          res.data.status === 'confirmed' ||
          (!res.data.clientSecret && (res.data.totalPriceCents ?? 0) <= 0);

        if (isFreeCheckout) {
          clearCheckoutSession();
          setCheckoutStep('success');
          return;
        }

        setMemberDiscountPercent(res.data.discountPercent ?? 0);
        setPendingTaxAmountCents(res.data.taxAmountCents ?? 0);
        setPendingTaxInclusive(res.data.taxInclusive ?? false);
        setPendingAmountCents(res.data.totalPriceCents ?? estimatedTotalCents);
        setPendingCurrency(rate?.currency ?? 'USD');
        setPendingPayment(res.data);
        setHeldBookingSelection({
          bookingId: res.data.bookingId,
          courtId,
          bookingDate,
          startTime: normalizedStart,
          durationMinutes,
        });
        setCheckoutStep('payment');
      } else {
        toast.error(res.error || 'Booking failed');
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  const waitingForOrgSlug = preferOrgApi && !resolvedOrgSlug;
  const pageLoading = waitingForOrgSlug || gridLoading || (slugResolving && !resolvedOrgSlug);

  if (pageLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-label="Loading" />
      </div>
    );
  }

  const clubTitle = orgProfile?.organization.name ?? club?.name ?? 'Court hire';
  const bannerUrl = orgProfile?.organization.bannerUrl ?? club?.bannerUrl;

  return (
    <>
      {!minimalChrome ? (
        <PublicOrgSubpageHero
          bannerUrl={bannerUrl}
          title="Book a court"
          headingId="court-hire-hero-heading"
        >
          <p className="mt-3 max-w-2xl text-sm text-white/85 sm:text-base">
            Reserve a court at {clubTitle} online — no account required.
          </p>
        </PublicOrgSubpageHero>
      ) : null}

      <div className={minimalChrome ? undefined : 'bg-muted/30'}>
        <div
          className={
            minimalChrome ? undefined : 'mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-10'
          }
        >

      {courtOptions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center px-6 py-12 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <MapPin className="h-8 w-8 text-muted-foreground" aria-hidden />
            </div>
            <h2 className="text-lg font-semibold text-foreground">No courts available</h2>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              {availabilityMessage ??
                'Online court hire is not available right now. Please check back later or contact the club directly.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
          <Card className="overflow-hidden border-border/80 bg-card shadow-sm">
            <CardHeader className="border-b border-border bg-muted/20 px-6 py-5">
              <CardTitle className="text-lg">
                {checkoutStep === 'success'
                  ? 'Booking confirmed'
                  : checkoutStep === 'payment'
                    ? 'Complete payment'
                    : 'Booking details'}
              </CardTitle>
              <CardDescription>
                {checkoutStep === 'success'
                  ? 'Your court is reserved. We sent a confirmation to your email and mobile.'
                  : checkoutStep === 'payment'
                    ? 'Enter your card details to confirm the reservation.'
                    : useMemberCheckout
                      ? 'Choose your court and time.'
                      : 'Choose your court, time, and contact information.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {checkoutStep === 'success' ? (
                <div className="space-y-5 py-4 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
                    <CheckCircle2 className="h-9 w-9 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-xl font-semibold text-foreground">You&apos;re all set!</h2>
                    <p className="text-sm text-muted-foreground">
                      Your court hire at {clubTitle} is confirmed
                      {selectedCourt ? ` for ${selectedCourt.name}` : ''}
                      {bookingSummary ? ` on ${bookingSummary.dateLabel} at ${startTime}` : ''}.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      A confirmation has been sent to <span className="font-medium text-foreground">{guestEmail}</span>
                      {' '}and your mobile number.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCheckoutStep('details');
                      clearCheckoutSession();
                    }}
                  >
                    Book another court
                  </Button>
                </div>
              ) : checkoutStep === 'payment' && pendingPayment?.clientSecret ? (
                <div className="space-y-4">
                  {memberDiscountPercent > 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Member discount ({memberDiscountPercent}%) applied to court hire.
                    </p>
                  ) : null}
                  {pendingTaxAmountCents > 0 ? (
                    <div className="flex justify-between text-sm text-foreground">
                      <span>{pendingTaxInclusive ? 'Includes tax:' : 'Tax:'}</span>
                      <span>{formatCents(pendingTaxAmountCents, pendingCurrency)}</span>
                    </div>
                  ) : null}
                  <PublicStripePaymentConfirm
                  clientSecret={pendingPayment.clientSecret}
                  stripeConnectAccountId={pendingPayment.stripeConnectAccountId}
                  amountCents={pendingAmountCents}
                  currency={pendingCurrency}
                  onSuccess={() => {
                    clearCheckoutSession();
                    setCheckoutStep('success');
                  }}
                  onCancel={() => {
                    setCheckoutStep('details');
                    if (bookingDate) {
                      if (preferOrgApi && resolvedOrgSlug) {
                        void refetchOrgGrid();
                      } else if (guestCheckoutSlug) {
                        void clubGridQuery.refetch();
                      }
                    }
                  }}
                />
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  <section className="space-y-4" aria-labelledby="court-time-heading">
                    <SectionHeading
                      icon={Calendar}
                      title="Court & time"
                      description="Select when you would like to play"
                    />
                    <div className="space-y-4 pl-0 sm:pl-12">
                      <Field>
                        <FieldLabel htmlFor="court-id">Court</FieldLabel>
                        <Select
                          key={courtOptions.map((court) => court.id).join(',')}
                          value={courtId || undefined}
                          onValueChange={setCourtId}
                          required
                        >
                          <SelectTrigger id="court-id" className="w-full">
                            <SelectValue placeholder="Select a court" />
                          </SelectTrigger>
                          <SelectContent>
                            {courtOptions.map((court) => (
                              <SelectItem key={court.id} value={court.id}>
                                {court.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>

                      {availabilityCheck && !availabilityCheck.available ? (
                        <p className="text-sm text-destructive" role="alert">
                          {availabilityCheck.reason}
                        </p>
                      ) : activeHeldBooking ? (
                        <p className="text-sm text-muted-foreground">
                          This court is held for you while you complete payment.
                        </p>
                      ) : gridLoading ? (
                        <p className="text-sm text-muted-foreground">Checking availability…</p>
                      ) : null}

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field>
                          <FieldLabel htmlFor="booking-date">Date</FieldLabel>
                          <Input
                            id="booking-date"
                            type="date"
                            value={bookingDate}
                            onChange={(e) => handleBookingDateChange(e.target.value)}
                            min={new Date().toISOString().slice(0, 10)}
                            required
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="start-time">Start time</FieldLabel>
                          <Input
                            id="start-time"
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            required
                          />
                        </Field>
                      </div>

                      <Field>
                        <FieldLabel htmlFor="duration">Duration</FieldLabel>
                        <Select
                          value={String(durationMinutes)}
                          onValueChange={(value) => setDurationMinutes(Number(value))}
                        >
                          <SelectTrigger id="duration" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DURATION_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={String(option.value)}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  </section>

                  {hasHireableEquipment ? (
                    <>
                      <Separator />

                      <section className="space-y-4" aria-labelledby="equipment-heading">
                        <SectionHeading
                          icon={Package}
                          title="Equipment add-ons"
                          description="Optional hireable equipment for your session"
                        />
                        <div className="pl-0 sm:pl-12">
                          <CourtHireEquipmentAddons
                            clubSlug={addonClubSlug ?? ''}
                            startAt={bookingWindow.startAt}
                            endAt={bookingWindow.endAt}
                            selected={selectedAddons}
                            onChange={setSelectedAddons}
                            availableAddons={availableAddons}
                            loadingAddons={false}
                          />
                        </div>
                      </section>
                    </>
                  ) : null}

                  <Separator />

                  {useMemberCheckout ? (
                    <section className="space-y-2 rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                      <p>
                        Booking as{' '}
                        <span className="font-medium text-foreground">
                          {user?.firstName} {user?.lastName}
                        </span>
                        . Confirmation will be sent to{' '}
                        <span className="font-medium text-foreground">{user?.email}</span>.
                      </p>
                      <p>Ensure your member profile has a valid mobile number before booking.</p>
                    </section>
                  ) : (
                  <section className="space-y-4" aria-labelledby="guest-details-heading">
                    <SectionHeading
                      icon={User}
                      title="Your details"
                      description="We will send your confirmation to this email"
                    />
                    <div className="space-y-4 pl-0 sm:pl-12">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field>
                          <FieldLabel htmlFor="guest-first">First name</FieldLabel>
                          <div className="relative">
                            <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                            <Input
                              id="guest-first"
                              className="pl-9"
                              value={guestFirstName}
                              onChange={(e) => setGuestFirstName(e.target.value)}
                              autoComplete="given-name"
                              required
                            />
                          </div>
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="guest-last">Last name</FieldLabel>
                          <Input
                            id="guest-last"
                            value={guestLastName}
                            onChange={(e) => setGuestLastName(e.target.value)}
                            autoComplete="family-name"
                            required
                          />
                        </Field>
                      </div>

                      <Field>
                        <FieldLabel htmlFor="guest-email">Email</FieldLabel>
                        <div className="relative">
                          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                          <Input
                            id="guest-email"
                            type="email"
                            className="pl-9"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            autoComplete="email"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <PhoneNumberLabel htmlFor="guest-phone" required purpose="club_court_hire">
                          Mobile number
                        </PhoneNumberLabel>
                        <PhoneInput
                          id="guest-phone"
                          value={guestPhone}
                          onChange={(value) => {
                            setGuestPhone(value);
                            if (phoneError) setPhoneError(null);
                          }}
                          defaultCountry={defaultPhoneCountry}
                          error={!!phoneError}
                          aria-invalid={!!phoneError}
                          aria-describedby={phoneError ? 'guest-phone-error' : undefined}
                        />
                        {phoneError ? (
                          <p id="guest-phone-error" className="mt-1 text-sm text-destructive" role="alert">
                            {phoneError}
                          </p>
                        ) : null}
                      </Field>
                    </div>
                  </section>
                  )}

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-muted-foreground">
                      By continuing, you agree to the club&apos;s booking terms.
                    </p>
                    <Button type="submit" className="w-full sm:w-auto" isLoading={submitting} disabled={!canSubmit}>
                      {requiresPayment ? (
                        <>
                          <CreditCard className="mr-2 h-4 w-4" aria-hidden />
                          Continue to payment
                        </>
                      ) : (
                        'Confirm booking'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          <aside className="lg:sticky lg:top-6">
            <Card className="overflow-hidden border-primary/20 shadow-sm">
              <CardHeader className="border-b border-border bg-primary/5 px-5 py-4">
                <CardTitle className="text-base">Booking summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-5">
                <div className="space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Court</span>
                    <span className="text-right font-medium text-foreground">
                      {selectedCourt?.name ?? '—'}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Date</span>
                    <span className="text-right font-medium text-foreground">
                      {bookingSummary?.dateLabel ?? '—'}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Time</span>
                    <span className="text-right font-medium text-foreground">
                      {bookingSummary?.timeLabel ?? '—'}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="text-right font-medium text-foreground">
                      {bookingSummary?.durationLabel ?? '—'}
                    </span>
                  </div>
                </div>

                <Separator />

                {hasPricing ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-muted-foreground">Court hire</span>
                      <span className="text-right font-medium text-foreground">
                        {hasMemberCourtDiscount ? (
                          <span className="inline-flex flex-col items-end gap-0.5">
                            <span className="text-xs font-normal text-muted-foreground line-through">
                              {formatCents(courtPriceCents, summaryCurrency)}
                            </span>
                            <span>{formatCents(discountedCourtCents, summaryCurrency)}</span>
                          </span>
                        ) : (
                          formatCents(courtPriceCents, summaryCurrency)
                        )}
                      </span>
                    </div>
                    {addonTotalCents > 0 ? (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-muted-foreground">Equipment</span>
                        <span className="text-right font-medium text-foreground">
                          {formatCents(addonTotalCents, summaryCurrency)}
                        </span>
                      </div>
                    ) : null}
                    {hasMemberCourtDiscount ? (
                      <div className="flex items-start justify-between gap-3 text-emerald-700 dark:text-emerald-400">
                        <span>Member discount ({effectiveDiscountPercent}%)</span>
                        <span className="text-right font-medium">
                          −{formatCents(courtDiscountCents, summaryCurrency)}
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">Total</span>
                  <span className="text-lg font-semibold text-foreground">
                    {hasPricing ? formatCents(displayTotalCents, summaryCurrency) : '—'}
                  </span>
                </div>

                {hasMemberCourtDiscount && listSubtotalCents !== displayTotalCents ? (
                  <p className="text-xs text-muted-foreground line-through">
                    Was {formatCents(listSubtotalCents, summaryCurrency)}
                  </p>
                ) : null}

                {selectedAddons.length > 0 && addonTotalCents > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Equipment add-ons are not discounted.
                  </p>
                ) : null}

                {rates.length > 0 ? (
                  <div className="rounded-lg bg-muted/50 p-3">
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" aria-hidden />
                      Court rates
                    </p>
                    <ul className="space-y-1.5">
                      {rates.map((rate) => {
                        const courtName =
                          courtOptions.find((court) => court.id === rate.courtId)?.name ?? rate.name;
                        const isSelected = rate.courtId === courtId || (!rate.courtId && rate.id === selectedRate?.id);
                        return (
                          <li
                            key={rate.id}
                            className={cn(
                              'flex items-center justify-between gap-2 text-xs',
                              isSelected ? 'font-medium text-foreground' : 'text-muted-foreground'
                            )}
                          >
                            <span className="truncate">
                              {courtName}
                              {rate.membersOnly ? ' (members)' : ''}
                            </span>
                            <span className="shrink-0">
                              {formatCents(rate.priceCents, rate.currency)}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </aside>
        </div>
      )}
        </div>
      </div>
    </>
  );
}

export function PublicClubCourtHireEmbedded() {
  return <PublicClubCourtHireContent embedded />;
}
